import React, { useEffect, useState } from "react";
import "@elastic/react-search-ui-views/lib/styles/styles.css";
import "./ElSearchAdvance.scss";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@me8eon/translation";
import { search, transformSearchResultsToDisplay } from "./ElSearchGetData";
import { SearchInput } from "./ElSearchAdvanceHelpers";
import { FilterSetters } from "./elsearch.domain";
import LoaderComponent from "../loader-component/LoaderComponent";
import { SearchRequest, useSearchFn } from "../../search/search";
import { FeedbackFn, useFeedback } from "@me8eon/feedback";
import { DataSourceFilter, ESApiFinalResponseInterface, MetadataFilters } from "../../interfaces/ElasticSearchInterface";
import { useFilterDebugFlag } from "../../hooks/useFilterDebugFlag";
import { FilterDebug } from "./filter-debug/filter.debug";
import { Scroll } from "../scroll";
import { useDebouncedCallback } from "@me8eon/debounce";
import { transformDataIntoDataSourceFiltersForM365 } from "../../domain/info/m365Info";
import { useComponents } from "./context/components.context";
import { DisplaySearchResults } from "@me8eon/data_views_search_results";
import { ElSearchAdvanceCardType } from "./results/ElSearchAdvanceCard";
import { useSelectedDataView, useSelectedDataViewName } from "@me8eon/data_view_filter";
import { ThemedIcon } from "@me8eon/icons";
import { DataViewNavBar } from "@me8eon/data_views_nav_bar";
import { needOffice } from "@me8eon/all_data_views";
import { filterDebugName, FilterScaffolding, searchDebugName, useAdditionalFiltersStateScaffolding, useFilter, useMetadataFiltersStateScaffolding, useSelectedMetadataFiltersStateScaffolding } from "@me8eon/filter_data_widget";
import SearchRefineResult from "./SearchRefineResult";
import { useDebug } from "@me8eon/debug";
import { scroll, searchParserFFName, submitSearch, useAllSearchData, useSearchParser, useSearchResults, useSearchResultsFromAllSearchData, useTriggerSearch } from "@me8eon/search";
import { useFeatureFlag } from "@me8eon/featureflags";
import { searchTextName } from "@me8eon/searchtext_filters";
import { BaseDataSourceDetails, useDataSources } from "@me8eon/data_sources";
import { useGuiFilters } from "@me8eon/filters";
import { elasticSearch } from "@me8eon/elastic_search_data_sources";
import { useSearchTextFilter } from "@me8eon/all_filters";
import { valueOrDefault } from "@me8eon/errors";
import { useJwtToken } from "@me8eon/react_authentication";
import { dataViewFilterName } from "@me8eon/data_views";
import { useUserData } from "@me8eon/react_authentication";
import { PageToggle } from "@me8eon/page_toggle";

function NoData({ noMoreData, searchTerm }: { noMoreData?: boolean, searchTerm?: string; }) {
    const t = useTranslation();
    return (
        <div className={"no-data"} key={"noData"}>
            {noMoreData ? <></> : <ThemedIcon name="search" scheme="red500" />}
            {t(`filterPage.filterMenu.${noMoreData ? "noMoreData" : "noData"}`)}
            {noMoreData ? "" : <>&nbsp;<strong>{searchTerm}</strong></>}
        </div>
    );
}

export function transformFoundCountsIntoDataFilters(searchIndex: string[], count: DataSourceFilter[]) {
    if (!searchIndex.length) return count;
    return searchIndex.map((index) => {
        const matchingCount = count.find((c) => c.key === index)?.doc_count ?? 0;
        return { key: index, doc_count: matchingCount };
    });
}


export type DisplayAllSearchResultsProps = {
    resultsToDisplay: any[];
    searchTerm: string;
    ElSearchAdvanceCard: ElSearchAdvanceCardType;
    noMoreData: boolean;
}

function DisplayAllSearchResults({ resultsToDisplay, searchTerm, ElSearchAdvanceCard, noMoreData }: DisplayAllSearchResultsProps) {
    const [allSearchData] = useAllSearchData();
    const searchResultEntries = allSearchData[submitSearch]?.searchResults ?? {};
    const firstKey = Object.keys(searchResultEntries)[0];
    const finished = valueOrDefault(searchResultEntries[firstKey], {} as any).finished;
    const end = finished;
    return <>
        <DisplaySearchResults rootId="filterPage.filterMenu" data={resultsToDisplay} />
        {end && <NoData noMoreData={resultsToDisplay.length > 0} searchTerm={searchTerm} />}
    </>;
}


export const ElSearchAdvance: React.FC = () => {
    const [dataView, setDataView] = useSelectedDataViewName();
    const dataSources = useDataSources();
    const [searchIndex, setSearchIndex] = useState<string[]>([]);
    const [additionalFilters, setAdditionalFilters] = useAdditionalFiltersStateScaffolding();
    const [searchTerm, setSearchTerm] = useSearchTextFilter();
    const [aiAssistNavbarClicked, setAiAssistNavbarClicked] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dataSourceFilters, setDataSourceFiltersRaw] = useState<DataSourceFilter[]>([]);
    const [m365DocTypes, setM365DocTypes] = useState<string[]>([]);
    const [resultsToDisplay, setResultsToDisplay] = useSearchResults();
    const [filterCount, setFilterCount] = useState<number>(0);
    const [showAiAssist, setShowAiAssist] = useState(false);
    const [metadataFilters, setMetadataFilters] = useMetadataFiltersStateScaffolding();
    const [selectedMetadataFilters, setSelectedMetadataFilters] = useSelectedMetadataFiltersStateScaffolding();
    const t = useTranslation();
    const searchFn = useSearchFn();
    const feedbackFn: FeedbackFn = useFeedback();
    const [noMoreData, setNoMoreData] = useState(false);
    const { ElSearchAdvanceCard } = useComponents(dataView);
    const [_f, setFilter] = useFilter();
    const currentDataView = useSelectedDataView<any, BaseDataSourceDetails>();
    const sDebug = useDebug(searchDebugName);
    const fDebug = useDebug(filterDebugName);
    fDebug("ElSearchAdvance", "additionalFilters", additionalFilters);
    const searchParserFF = useFeatureFlag(searchParserFFName);
    // const searchParserFF = featureFlagEnabled || useFeatureFlag(searchParserFFName);
    const srFromNewSearch = useSearchResultsFromAllSearchData(submitSearch);
    const trigger = useTriggerSearch();
    const [guiFilters, setGuiFilters] = useGuiFilters();
    const searchParser = useSearchParser();
    const [_, setSearchText] = useSearchTextFilter();
    const apiKey = useJwtToken();
    const location = useLocation();
    const userData = useUserData();

    useEffect(() => {
        setAdditionalFilters({});
    }, [dataView, searchTerm]);

    useEffect(() => {
        return () => {
            setDataView("all")
        };
    }, []);


    function onSearchTermChanged(newQuery: string) {

    }

    function onSubmit(query: string | null | undefined) {
        if (!query) {
            return;
        }
        const newFilters: any = searchParserFF
            ? searchParser({ ...guiFilters, [searchTextName]: query })!.filters
            : { ...guiFilters, [searchTextName]: query };
        setGuiFilters(newFilters);
        if (searchParserFF) {
            sDebug("SearchInput.querySubmit", newFilters);
            if (newFilters[dataViewFilterName] === dataView)
                trigger(submitSearch, newFilters);
        } else
            trigger(submitSearch, newFilters);
    }

    useEffect(() => {//Update the search term from the URL on page load
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const searchQuery = urlParams.get("q");
        onSubmit(searchQuery);
    }, [location]);

    const setters: FilterSetters = {
        setIsLoading,
        setResultsToDisplay,
        setDataSourceFilters: setDataSourceFiltersFromFound,
        setDataType: setDataView,
        setSearchIndex,
        setAdditionalFilters,
        setMetadataFilters,
        setFilterCount,
        setM365DocTypes,
    };

    const actualSearchIndex = currentDataView?.searchIndices || [];
    const allowOfficeHack = needOffice(currentDataView);

    const rawSearch = search(fDebug, true, {

        setIsLoading, setResultsToDisplay, searchTerm, resultsToDisplay, searchFn, setNoMoreData, transformSearchResultsToDisplay,
        query: (append: boolean, searchTerm: string, baseData: any[]): SearchRequest => ({
            append,
            apiKey,
            dataType: dataView,
            searchTerm,
            searchIndexes: actualSearchIndex,
            allowOfficeHack,
            ...(dataView === "m365" && m365DocTypes.length > 0 ? { m365DocTypes: m365DocTypes } : {}),
            additionalFilters,
            ...(baseData.length && baseData[baseData.length - 1].score && {
                searchAfter: [
                    baseData[baseData.length - 1].score,
                    baseData[baseData.length - 1].id,
                ],
            }),
            expectedRecordSize: 20,
        }),
        setDataSourceFiltersWrapper: setDataSourceFiltersFromFound,
        setMetadataFilters: setMetadataFiltersWrapper,
        metadataFilters,
        feedback: (searchTerm) => feedbackFn({ source: "query", page: "advanced", query: searchTerm, role: userData.currentRole || "unknown" }),
        atEnd: () => {
            if (actualSearchIndex.length) {
                setSelectedMetadataFilters(metadataFilters[actualSearchIndex[0]]);
            }
        },
    });
    const debouncedSearch = useDebouncedCallback(rawSearch, 500);

    useEffect(() => {
        fDebug("rawSearch", "additionalFilters are", additionalFilters);
        rawSearch(false); //no need to debounce as searchTerm is already debounced
    }, [searchTerm, searchIndex, additionalFilters, dataView]);


    useEffect(() => {
        if (dataView === "assistance") {
            setShowAiAssist(true);
        }
    }, [dataView]);


    function setDataSourceFiltersFromFound(data: ESApiFinalResponseInterface[], count: DataSourceFilter[]) {
        if (dataView === "m365")
            return setDataSourceFiltersRaw(transformDataIntoDataSourceFiltersForM365(data));
        setDataSourceFiltersRaw(transformFoundCountsIntoDataFilters(searchIndex, count));
    }

    function setMetadataFiltersWrapper(filters: MetadataFilters) {
        try {
            fDebug("setMetadataFiltersWrapper", "additionalFilters are", additionalFilters, "filters", filters);

            for (const key in additionalFilters.selectedMetadata) {
                if (!additionalFilters.selectedMetadata[key].length) continue;
                if (filters[actualSearchIndex[0]][key]) {
                    filters[actualSearchIndex[0]][key] = {
                        ...metadataFilters[actualSearchIndex[0]][key],
                    };
                }
            }
        } catch (e) {
            console.error(e, filters, metadataFilters);
        }
        setMetadataFilters(filters);
    }

    async function onScroll() {
        const details = currentDataView?.search[submitSearch]?.dataSource || [];
        const detail = details.find(d => d.type === elasticSearch);
        sDebug("onScroll", "detail", detail);
        if (detail) scroll(dataSources, submitSearch, detail, srOps, sDebug, userData);
    }

    const debouncedOnScroll = useDebouncedCallback(onScroll, 200);
    let filterDebug = useFilterDebugFlag();

    useEffect(() => {
        fDebug("setFilterScaffolding", "additionalFilters are", additionalFilters);
        const scaffolding: FilterScaffolding = {
            dataType: dataView as string,
            additionalFilters,
            selectedMetadataFilters,
            searchIndex: actualSearchIndex,
            dataSourceFilters,
            setters,
            filterCount,
            m365DocTypes,
            Display: SearchRefineResult,
        } as any as FilterScaffolding;
        setFilter(scaffolding);
    }, [searchTerm, additionalFilters, selectedMetadataFilters, searchIndex, dataSourceFilters, filterCount, dataView]);

    const fromNewSearch = srFromNewSearch;
    const realDisplayData = fromNewSearch;
    return showAiAssist || aiAssistNavbarClicked || dataView === "aiAssistant" ? (
        // <AiAssist
        //     initialPrompt={{ searchTerm, aiAssistNavbarClicked: aiAssistNavbarClicked || dataView === "aiAssistant" }}
        // />
        <></>
    ) : (
        <Scroll className="search-custom-filter-wrapper" onScroll={debouncedOnScroll}>
            <PageToggle />
            {/*<pre>Search term{searchTerm} GUI filters {JSON.stringify(guiFilters, null, 2)}</pre>*/}

            {filterDebug && <FilterDebug setters={setters}
                dataType={dataView}
                searchIndex={searchIndex}
                additionalFilters={additionalFilters}
                metadataFilters={metadataFilters}
                selectedMetadataFilters={selectedMetadataFilters}
                dataSourceFilters={dataSourceFilters}
                filterCount={filterCount} />}
            <div className="search-input">
                <SearchInput onSubmit={onSubmit} />
            </div>
            <DataViewNavBar rootId="filterPage.filterMenu" width={0} />
            {searchTerm === "" && (
                <div className="no-search-term">
                    <div style={{ textAlign: "center", width: "100%" }}>
                        <div>
                            <ThemedIcon name="search" scheme="red500" />
                        </div>
                        <p style={{ fontWeight: "bold" }}>
                            {t("filterPage.filterMenu.enterSearchTerm")}
                        </p>
                    </div>
                </div>
            )}
            <LoaderComponent isLoading={isLoading} isLinear />
            <div style={{ backgroundColor: "#e4e3e082" }}>
                {dataView !== "assistance" && searchTerm &&
                    <DisplayAllSearchResults resultsToDisplay={realDisplayData} searchTerm={searchTerm} ElSearchAdvanceCard={ElSearchAdvanceCard} noMoreData={noMoreData} />}
                {isLoading && (
                    <LoaderComponent isLoading={isLoading} isLinear />
                )}
            </div>
        </Scroll>
    );
};
